import { useContext, useEffect, useState } from "react";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import { AlertModalBinIcon } from "../../../assets";
import { AppLayout } from "../../../containers/layout/app";
import { MFIUserWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { BaseButton } from "../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { MFIUserTable } from "../../../containers/tables/mfiusertable";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllMFIUsersService } from "../../../utils/api/usermanagement/mfiuser/retrieveAllMFIUsers";
import { deleteMFIUserService } from "../../../utils/api/usermanagement/mfiuser/deleteMFI";
import { BaseAlertModal } from "../../../components/modal/alert";

export const MFIUser = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);

	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [mfiUserIdToDelete, setMFIUserIdToDelete] = useState<string | null>(
		null,
	);
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("User Management");
	}, [setActiveNavItem]);

	const handleAddUserButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate("/usermanagement/addmfiuser");
	};

	const { data: mfiUsers, refetch: refetchMFIUsers } = useQuery({
		queryKey: ["mfiUsers", TOKEN, paginationIndex],
		queryFn: async () => {
			try {
				setIsFetching(true);
				const mfiUsers = await retrieveAllMFIUsersService(
					TOKEN,
					paginationIndex,
				);
				if (!Array.isArray(mfiUsers) && mfiUsers?.meta) {
					setPaginationIndex((prev) => ({
						...prev,
						totalPages: String(mfiUsers?.meta?.totalPages ?? "1"),
					}));
				}
				setIsFetching(false);
				return mfiUsers?.data;
			} catch (error) {
				console.error("Error fetching MFI users:", error);
				throw error;
			}
		},
		enabled: !!TOKEN,
	});

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		return setIsAlertModalOpen(false);
	};

	const handleDeleteMFIUser = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		institutionId: string,
		mfiUserId: string,
	) => {
		e.preventDefault();
		setError(null);
		setMFIUserIdToDelete(mfiUserId);
		try {
			const response = await deleteMFIUserService(
				TOKEN,
				institutionId,
				mfiUserId,
			);
			if (response.status === "success") {
				setMFIUserIdToDelete(null);
				setIsAlertModalOpen(true);
				refetchMFIUsers();
			} else {
				setMFIUserIdToDelete(null);
				setError("MFI User deletion failed. Please try again.");
			}
		} catch (error: any) {
			setMFIUserIdToDelete(null);
			setError(`MFI User deletion failed. ${error.message}`);
			console.error("MFI User deletion failed:", error);
		}
	};

	const handleActionClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string | Record<any, any>,
		action?: string,
	) => {
		e.preventDefault();
		switch (action) {
			case "edit":
				return navigate(`/usermanagement/mfiuser/${id}`);
			case "delete":
				if (typeof id !== "string") {
					const { institutionId, mfiUserId } = id;
					return handleDeleteMFIUser(e, institutionId, mfiUserId);
				}
				break;
			default:
				console.warn(`Unhandled action: ${action}`);
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	return (
		<AppLayout pageId={"MFI-User"} pageTitle={"MFI User"}>
			<MFIUserWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalBinIcon />}
					title="User Successfully Deleted"
					callToAction="Close"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<BaseButton
						variant="contained"
						bgcolor="var(--primary-color)"
						padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
						startIcon={<AddIcon />}
						sx={{
							width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							float: { miniTablet: "right" },
						}}
						onClick={handleAddUserButtonClick}
					>
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Add User
						</Typography>
					</BaseButton>
				</Box>
				<Box component={"div"} className="mfi-user-table-box">
					<MFIUserTable
						rows={mfiUsers as Record<any, any>[]}
						selectedMFIUserId={mfiUserIdToDelete}
						handleItemClick={handleActionClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
				{error && (
					<Typography
						fontFamily={"Inter"}
						fontWeight={"600"}
						fontSize={14}
						lineHeight={"normal"}
						color={"var(--text-salmon-pink-color)"}
						whiteSpace={"normal"}
					>
						{error}
					</Typography>
				)}
			</MFIUserWrapper>
		</AppLayout>
	);
};
